import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { JobProvider } from './contexts/JobContext';
import ProtectedRoute from './components/ProtectedRoute';
import BootSequence from './components/BootSequence';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import CreateArticle from './pages/CreateArticle';
import WordPressSites from './pages/WordPressSites';
import ArticlePreview from './pages/ArticlePreview';
import Publications from './pages/Publications';
import AutoEditoExpress from './pages/AutoEditoExpress';
import CostDashboard from './pages/CostDashboard';
import Help from './pages/Help';
import Tribunes from './pages/Tribunes';
import Partenaires from './pages/Partenaires';
import Interviews from './pages/Interviews';
import CommuniquesPresse from './pages/CommuniquesPresse';
import UserGuide from './pages/UserGuide';
import Equipage from './pages/Equipage';
import Periscope from './pages/Periscope';
import VeillePage from './pages/VeillePage';
import AmazonDeals from './pages/AmazonDeals';
import ScoutMedia from './pages/ScoutMedia';
import PublicationQueue from './pages/PublicationQueue';
import LLMArtifacts from './pages/LLMArtifacts';
import InterviewTracking from './pages/InterviewTracking';
import AutoDossier from './pages/AutoDossier';
import Discover from './pages/Discover';
import PromptsManager from './pages/PromptsManager';
import ApiConnectionsManager from './pages/ApiConnectionsManager';
import PublicSubmit from './pages/PublicSubmit';
import CiblesPage from './pages/CiblesPage';
import ShareTarget from './pages/ShareTarget';
import ConduitePage from './pages/ConduitePage';
import ConduiteHistoryPage from './pages/ConduiteHistoryPage';
import ErrorBoundary from './components/ErrorBoundary';
import { Home, User, FileText, Globe, History, Rocket, LogOut, Euro, HelpCircle, Terminal, Mail, Book, Radar, Radio, Users, Binoculars, Crosshair, MessageSquare, ShoppingCart, Compass, Handshake, CalendarClock, Network, Sparkles, Newspaper, ScanSearch, Target, LayoutDashboard } from 'lucide-react';
import ActiveJobsIndicator from './components/ActiveJobsIndicator';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const STATIC_URL = BACKEND_URL; // For static files like images

const Navigation = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const { isDark, colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [survOpen, setSurvOpen] = useState(false);
  
  const isAmiral = user?.grade === 'amiral';
  const isCapitaineOrAbove = ['amiral', 'capitaine'].includes(user?.grade);
  
  const navItems = [
    { path: '/', icon: Home, label: 'Dashboard' },
    { path: '/conduite', icon: LayoutDashboard, label: 'Conduite' },
    { path: '/cibles', icon: Target, label: 'Cibles' },
    { path: '/create', icon: FileText, label: 'Creer' },
    { path: '/auto-dossier', icon: Network, label: 'Dossier' },
    { path: '/periscope', icon: Binoculars, label: 'Periscope' },
    ...(isCapitaineOrAbove ? [{ path: '/scout', icon: Compass, label: 'Scout' }] : []),
    ...(isCapitaineOrAbove ? [{ path: '/amazon', icon: ShoppingCart, label: 'Amazon' }] : []),
    ...(isAmiral ? [{ type: 'surveillance' }] : []),
    ...(isCapitaineOrAbove ? [{ path: '/tribunes', icon: Mail, label: 'Tribunes' }] : []),
    ...(isCapitaineOrAbove ? [{ path: '/partenaires', icon: Handshake, label: 'Partenaires' }] : []),
    ...(isCapitaineOrAbove ? [{ path: '/interviews', icon: MessageSquare, label: 'Interviews' }] : []),
    ...(isCapitaineOrAbove ? [{ path: '/communiques-presse', icon: Newspaper, label: 'Auto-CP' }] : []),
    ...(isCapitaineOrAbove ? [{ path: '/discover', icon: Sparkles, label: 'Discover' }] : []),
    { path: '/wordpress', icon: Globe, label: 'WordPress' },
    { path: '/publications', icon: History, label: 'Publications' },
    ...(isCapitaineOrAbove ? [{ path: '/costs', icon: Euro, label: 'Couts' }] : []),
    ...(isAmiral ? [{ path: '/equipage', icon: Users, label: 'Equipage' }] : []),
    { path: '/guide', icon: Book, label: 'Guide' },
    { path: '/help', icon: HelpCircle, label: 'Aide' },
    { path: '/profile', icon: User, label: 'Profil' },
  ];

  const surveillanceItems = [
    { path: '/veille', icon: ScanSearch, label: 'Surveillance' },
    { path: '/publication-queue', icon: CalendarClock, label: 'Planif.' },
  ];

  const isSurveillancePath = location.pathname === '/veille' || location.pathname === '/publication-queue';
  
  return (
    <>
      {/* Mobile Menu Button - Tactical Navy */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-slate-950/90 backdrop-blur-xl border border-sky-500/50 rounded shadow-[0_0_12px_rgba(56,189,248,0.2)]"
        data-testid="mobile-menu-toggle"
      >
        <Rocket className="w-6 h-6 text-sky-400" />
      </button>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Navigation - Tactical Navy Style */}
      <nav 
        className={`fixed left-0 top-0 h-full backdrop-blur-xl border-r border-sky-900/40 z-50 transition-all duration-300 ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${collapsed ? 'w-16' : 'w-48'}`}
        style={{
          backgroundColor: isDark ? 'rgba(2, 6, 23, 0.90)' : 'rgba(255, 255, 255, 0.95)',
          borderColor: isDark ? 'rgba(30, 58, 95, 0.6)' : colors.borderPrimary
        }}
      >
        <div className="flex flex-col items-center py-4 space-y-1 h-full overflow-y-auto overflow-x-hidden">
          {/* Logo + Toggle - Tactical Navy */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="mb-3 p-2 rounded hover:bg-sky-950/30 transition-colors"
            data-testid="sidebar-toggle"
            title={collapsed ? 'Déplier le menu' : 'Replier le menu'}
          >
            <Rocket className="w-7 h-7 text-sky-400" strokeWidth={1.5} />
          </button>
          
          {navItems.map((item, idx) => {
            if (item.type === 'surveillance') {
              return (
                <div key="surveillance" className="w-full px-2">
                  <button
                    onClick={() => setSurvOpen(!survOpen)}
                    data-testid="nav-surveillance"
                    className={`relative group flex items-center ${collapsed ? 'justify-center' : 'justify-start gap-3 px-2'} w-full h-10 rounded transition-all duration-300 ${
                      isSurveillancePath
                        ? 'bg-sky-500/20 text-sky-300 shadow-[0_0_12px_rgba(56,189,248,0.3)] border-l-2 border-sky-400'
                        : 'text-slate-400 hover:text-sky-300 hover:bg-sky-950/30'
                    }`}
                  >
                    {isSurveillancePath && <div className="absolute left-0 w-1 h-full bg-sky-400 glow-sky rounded-r" />}
                    <Radar className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                    {!collapsed && <span className="text-xs font-rajdhani tracking-wider uppercase font-semibold whitespace-nowrap">Surveill.</span>}
                  </button>
                  {(survOpen || isSurveillancePath) && (
                    <div className={`flex flex-col gap-0.5 mt-0.5 ${collapsed ? 'items-center' : 'pl-4'}`}>
                      {surveillanceItems.map(sub => {
                        const SubIcon = sub.icon;
                        const isSubActive = location.pathname === sub.path;
                        return (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            onClick={() => setIsOpen(false)}
                            data-testid={`nav-${sub.label.toLowerCase()}`}
                            className={`flex items-center ${collapsed ? 'justify-center w-8 h-8' : 'gap-3 px-2 h-8'} rounded transition-all duration-300 ${
                              isSubActive
                                ? 'bg-sky-500/20 text-sky-300'
                                : 'text-slate-500 hover:text-sky-300 hover:bg-sky-950/30'
                            }`}
                          >
                            <SubIcon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                            {!collapsed && <span className="text-[10px] font-rajdhani tracking-wider uppercase whitespace-nowrap">{sub.label}</span>}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <div key={item.path} className="w-full px-2">
                <Link
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`relative group flex items-center ${collapsed ? 'justify-center' : 'justify-start gap-3 px-2'} w-full h-10 rounded transition-all duration-300 ${
                    isActive 
                      ? 'bg-sky-500/20 text-sky-300 shadow-[0_0_12px_rgba(56,189,248,0.3)] border-l-2 border-sky-400' 
                      : 'text-slate-400 hover:text-sky-300 hover:bg-sky-950/30'
                  }`}
                >
                  {isActive && (
                    <div className="absolute left-0 w-1 h-full bg-sky-400 glow-sky rounded-r" />
                  )}
                  <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  {!collapsed && (
                    <span className="text-xs font-rajdhani tracking-wider uppercase font-semibold whitespace-nowrap">
                      {item.label}
                    </span>
                  )}
                </Link>
              </div>
            );
          })}
          
          {/* Indicateur de jobs actifs (visible quand des générations sont en cours) */}
          {!collapsed && (
            <div className="mt-auto pt-4 px-2">
              <ActiveJobsIndicator />
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

const AppLayout = ({ children }) => {
  const { isDark, colors } = useTheme();
  const [collapsed, setCollapsed] = useState(true);
  
  return (
    <div 
      className="min-h-screen relative flex flex-col"
      style={{ backgroundColor: colors.bgPrimary }}
    >
      {/* Scanlines overlay - only in dark mode */}
      {isDark && (
        <div className="fixed inset-0 pointer-events-none opacity-[0.02] z-50"
             style={{
               backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,255,0.05) 2px, rgba(0,255,255,0.05) 4px)'
             }}
        />
      )}
      <Navigation collapsed={collapsed} setCollapsed={setCollapsed} />
      <div className={`relative z-10 flex-1 transition-all duration-300 ${collapsed ? 'lg:ml-16' : 'lg:ml-48'}`}>
        {children}
      </div>
      {/* Footer - Tactical Navy */}
      <footer 
        className={`relative z-10 border-t border-sky-900/40 py-4 px-8 text-center transition-all duration-300 ${collapsed ? 'lg:ml-16' : 'lg:ml-48'}`}
        style={{ borderColor: isDark ? 'rgba(30, 58, 95, 0.6)' : colors.borderPrimary }}
      >
        <p 
          className="text-xs md:text-sm font-mono"
          style={{ color: colors.textMuted }}
        >
          © 2026 <span className="text-sky-500">LPP Logiciel</span> & <span className="text-sky-500">Jean-Baptiste Giraud</span> & <span className="text-sky-500">Paolo Garoscio</span> | SYSTÈME v3.9.0
        </p>
      </footer>
    </div>
  );
};

// Boot sequence wrapper
const BootWrapper = ({ children }) => {
  const { showBoot, pendingUser, completeBoot } = useAuth();
  
  if (showBoot && pendingUser) {
    return <BootSequence user={pendingUser} onComplete={completeBoot} />;
  }
  
  return children;
};

const AmiralRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.grade !== 'amiral') return <Navigate to="/" replace />;
  return children;
};

const CapitaineRoute = ({ children }) => {
  const { user } = useAuth();
  if (!['amiral', 'capitaine'].includes(user?.grade)) return <Navigate to="/" replace />;
  return children;
};

// Wrapper component for theme-aware Toaster
const ThemedToaster = () => {
  const { isDark } = useTheme();
  return <Toaster position="top-right" theme={isDark ? "dark" : "light"} />;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <JobProvider>
            <div className="App">
              <BrowserRouter>
                <BootWrapper>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    {/* Route publique pour soumettre des cibles (sans auth) */}
                    <Route path="/s/:token" element={<PublicSubmit />} />
                    <Route path="/*" element={
                      <ProtectedRoute>
                        <AppLayout>
                          <Routes>
                            <Route path="/" element={<Dashboard />} />
                            <Route path="/conduite" element={<ConduitePage />} />
                            <Route path="/conduite/historique" element={<ConduiteHistoryPage />} />
                            <Route path="/auto-edito" element={<AutoEditoExpress />} />
                            <Route path="/profile" element={<Profile />} />
                            <Route path="/create" element={<CreateArticle />} />
                            <Route path="/creer" element={<Navigate to="/create" replace />} />
                            <Route path="/auto-dossier" element={<AutoDossier />} />
                            <Route path="/wordpress" element={<WordPressSites />} />
                            <Route path="/article/:id" element={<ArticlePreview />} />
                            <Route path="/publications" element={<Publications />} />
                            <Route path="/cibles" element={<CiblesPage />} />
                            <Route path="/share" element={<ShareTarget />} />
                            <Route path="/tribunes" element={<CapitaineRoute><Tribunes /></CapitaineRoute>} />
                            <Route path="/partenaires" element={<CapitaineRoute><Partenaires /></CapitaineRoute>} />
                            <Route path="/interviews" element={<CapitaineRoute><Interviews /></CapitaineRoute>} />
                            <Route path="/communiques-presse" element={<CapitaineRoute><CommuniquesPresse /></CapitaineRoute>} />
                            <Route path="/discover" element={<CapitaineRoute><Discover /></CapitaineRoute>} />
                            <Route path="/veille" element={<AmiralRoute><VeillePage /></AmiralRoute>} />
                            <Route path="/publication-queue" element={<AmiralRoute><PublicationQueue /></AmiralRoute>} />
                            <Route path="/periscope" element={<Periscope />} />
                            <Route path="/amazon" element={<CapitaineRoute><AmazonDeals /></CapitaineRoute>} />
                            <Route path="/scout" element={<CapitaineRoute><ScoutMedia /></CapitaineRoute>} />
                            <Route path="/guide" element={<UserGuide />} />
                            <Route path="/costs" element={<CapitaineRoute><CostDashboard /></CapitaineRoute>} />
                            <Route path="/equipage" element={<AmiralRoute><Equipage /></AmiralRoute>} />
                            <Route path="/admin/llm-artifacts" element={<AmiralRoute><LLMArtifacts /></AmiralRoute>} />
                            <Route path="/admin/interview-tracking" element={<AmiralRoute><InterviewTracking /></AmiralRoute>} />
                            <Route path="/admin/prompts" element={<AmiralRoute><PromptsManager /></AmiralRoute>} />
                            <Route path="/admin/api-connections" element={<AmiralRoute><ApiConnectionsManager /></AmiralRoute>} />
                            <Route path="/help" element={<Help />} />
                          </Routes>
                        </AppLayout>
                      </ProtectedRoute>
                    } />
                  </Routes>
                </BootWrapper>
              </BrowserRouter>
              <ThemedToaster />
            </div>
          </JobProvider>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
export { toast };